import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { PatientAPI } from "../../services/patient.service";
import { GrowthChartAPI, type GrowthChartResponse, type GrowthIndicator } from "../../services/growth-chart.service";

interface GuardianContact {
  emergency_contact_id: string;
  name: string;
}

interface GuardianShareModalProps {
  patientId: string;
  patientName: string;
  indicator: GrowthIndicator;
  chart: GrowthChartResponse;
  onCapturePng: () => Promise<string>;
  onClose: () => void;
}

// Values-and-reassurance draft, per the ticket: plain language, no raw
// "red flag" wording. Flag distribution informs tone, never a clinical term.
function draftMessage(patientName: string, chart: GrowthChartResponse): string {
  const latest = chart.patient_series[chart.patient_series.length - 1];
  if (!latest) {
    return `Hi, here is ${patientName}'s latest growth chart from today's visit.`;
  }
  const valueLine = `${patientName}'s latest measurement is ${latest.value} ${chart.value_unit}.`;
  if (latest.flag === "green") {
    return `Hi, ${valueLine} This is within the expected range for ${patientName}'s age — great progress! Please continue with regular check-ups.`;
  }
  if (latest.flag === "amber") {
    return `Hi, ${valueLine} We'd like to keep a closer eye on this over the next few visits — nothing urgent, just routine monitoring. Please continue with your scheduled check-ups.`;
  }
  if (latest.flag === "out_of_range") {
    // Not a clinical concern - the value just falls outside this chart's
    // plottable range, so there's no percentile/flag to report at all.
    return `Hi, ${valueLine} We'll continue tracking ${patientName}'s growth at upcoming visits.`;
  }
  return `Hi, ${valueLine} We'd like to discuss this measurement with you at your earliest convenience. Please reach out to the clinic to schedule a follow-up.`;
}

export const GuardianShareModal: React.FC<GuardianShareModalProps> = ({
  patientId,
  patientName,
  indicator,
  chart,
  onCapturePng,
  onClose,
}) => {
  const [guardians, setGuardians] = useState<GuardianContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [message, setMessage] = useState(() => draftMessage(patientName, chart));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    PatientAPI.getById(patientId)
      .then((patient) => {
        const eligible = (patient.emergency_contacts ?? [])
          .filter((c: any) => c.is_guardian && c.guardian_whatsapp_consent)
          .map((c: any) => ({ emergency_contact_id: c.emergency_contact_id, name: c.name }));
        setGuardians(eligible);
        if (eligible.length > 0) setSelectedContactId(eligible[0].emergency_contact_id);
      })
      .catch(() => setGuardians([]))
      .finally(() => setLoadingContacts(false));
  }, [patientId]);

  useEffect(() => {
    let cancelled = false;
    onCapturePng().then((base64) => {
      if (!cancelled) setPreviewUrl(`data:image/png;base64,${base64}`);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSend = useMemo(
    () => !!selectedContactId && message.trim().length > 0 && !sending,
    [selectedContactId, message, sending]
  );

  const handleSend = async () => {
    if (!selectedContactId) {
      toast.error("Select a guardian to send to");
      return;
    }
    setSending(true);
    try {
      const png = await onCapturePng();
      const result = await GrowthChartAPI.share(patientId, {
        emergency_contact_id: selectedContactId,
        indicator,
        message: message.trim(),
        chart_image_base64: png,
      });
      toast.success(`Growth chart sent to ${result.sent_to_masked}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send growth chart");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Share growth chart with guardian</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        {previewUrl && (
          <img src={previewUrl} alt="Growth chart preview" className="w-full rounded-lg border border-slate-200" />
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-600">Send to</label>
          {loadingContacts ? (
            <p className="text-xs text-slate-500">Loading guardian contacts…</p>
          ) : guardians.length === 0 ? (
            <p className="text-xs text-red-600">
              No guardian with WhatsApp consent found. Mark a contact as guardian and record consent in the
              Emergency Contacts section first.
            </p>
          ) : (
            <select
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {guardians.map((g) => (
                <option key={g.emergency_contact_id} value={g.emergency_contact_id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-600">Message (edit before sending)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend || guardians.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Confirm & send"}
          </button>
        </div>
      </div>
    </div>
  );
};
